import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

/**
 * MarkdownRenderer — renders message content as Markdown with GFM support.
 *
 * Handles: bold, italic, underline, strikethrough, inline code, code
 * blocks, blockquotes, lists, auto-linked URLs, and custom @mention
 * highlighting.
 *
 * Mentions follow ADR-0044's two-format coexistence rule (§5):
 *   - **Sentinel form** `@[Display Name](user:uuid)` (emitted by Lexical
 *     composer + lexical-beautiful-mentions per KAP-303) — react-markdown
 *     parses as a CommonMark link with `href="user:<uuid>"`; the `a`
 *     renderer below detects the `user:` scheme and renders a styled chip.
 *     The leading `@` in the source markdown lands as a sibling text node;
 *     the `p` renderer strips trailing `@` that immediately precedes a
 *     mention chip to avoid the doubled `@` glyph.
 *   - **Legacy bare-`@token`** (already in DynamoDB pre-KAP-303) — the
 *     `MentionText` text-walker still splits on `/(@[\w.-]+)/g` and wraps
 *     each match in a styled non-linked span. Zero backfill, zero
 *     misattribution risk.
 */

function MentionText({ children }) {
  if (typeof children !== 'string') return children;

  // Split on bare-`@token` mentions — word characters, hyphens, dots.
  // Sentinel-form mentions never reach here: react-markdown lifts them
  // into `<a>` nodes before the `p` renderer maps children.
  const parts = children.split(/(@[\w.-]+)/g);
  return parts.map((part, i) => {
    if (!part.startsWith('@')) return part;
    const name = part.slice(1).toLowerCase();
    const isAI = name === 'kapsaai' || name === 'kapsa-ai' || name === 'kapsa_ai';
    return (
      <span
        key={i}
        className={isAI ? 'md-mention md-mention-ai' : 'md-mention md-mention-user'}
      >
        {part}
      </span>
    );
  });
}

/**
 * Detect a rendered mention-chip child. Used by the `p` renderer below to
 * decide whether to strip a trailing `@` from the preceding text node.
 * (The chip itself carries the `@` prefix; without this strip we'd render
 * a duplicate `@@Name`.)
 */
function isMentionChip(child) {
  return (
    React.isValidElement(child) &&
    typeof child.props?.href === 'string' &&
    child.props.href.startsWith('user:')
  );
}

const markdownComponents = {
  p({ children }) {
    // Two passes:
    //   1. Walk children, look-ahead one slot. If a string child ends in '@'
    //      AND the next child is a mention-chip, strip the trailing '@'.
    //   2. Wrap remaining string children with MentionText so the legacy
    //      bare-`@token` regex still styles old messages.
    const childArray = React.Children.toArray(children);
    const stripped = childArray.map((child, idx) => {
      if (typeof child !== 'string') return child;
      const nextChild = childArray[idx + 1];
      if (isMentionChip(nextChild) && child.endsWith('@')) {
        return child.slice(0, -1);
      }
      return child;
    });
    return (
      <p className="md-paragraph">
        {stripped.map((child, i) =>
          typeof child === 'string' ? (
            <MentionText key={`mt-${i}`}>{child}</MentionText>
          ) : (
            React.cloneElement(child, { key: child.key || `c-${i}` })
          )
        )}
      </p>
    );
  },
  // KAP-303 — render the ADR-0044 sentinel `@[Display Name](user:uuid)` as a
  // styled mention chip when the href uses the `user:` scheme. Falls through
  // to the standard external link rendering for everything else.
  a({ href, children }) {
    if (typeof href === 'string' && href.startsWith('user:')) {
      const userId = href.slice('user:'.length);
      const isAI = userId === 'KapsaAI';
      return (
        <span
          className={`md-mention md-mention-chip${isAI ? ' md-mention-ai' : ' md-mention-user'}`}
          data-user-id={userId}
          role="link"
        >
          @{children}
        </span>
      );
    }
    return (
      <a className="md-link" href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
  // react-markdown v9+ removed the `inline` prop from custom `code`
  // components. Detect inline vs fenced by the `language-*` className that
  // remark-gfm sets only on fenced blocks. react-markdown wraps fenced
  // blocks in `<pre>` automatically, so we only render the inner `<code>`
  // here and style each form independently.
  code({ className, children, ...props }) {
    const isBlock = typeof className === 'string' && className.startsWith('language-');
    return (
      <code
        className={isBlock ? `md-code-block-text ${className}` : 'md-inline-code'}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre({ children }) {
    return <pre className="md-code-block">{children}</pre>;
  },
  blockquote({ children }) {
    return <blockquote className="md-blockquote">{children}</blockquote>;
  },
  ul({ children }) {
    return <ul className="md-list">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="md-list md-list-ordered">{children}</ol>;
  },
  li({ children }) {
    return <li className="md-list-item">{children}</li>;
  },
  strong({ children }) {
    return <strong className="md-bold">{children}</strong>;
  },
  em({ children }) {
    return <em className="md-italic">{children}</em>;
  },
  del({ children }) {
    return <del className="md-strikethrough">{children}</del>;
  },
  // KAP-300 fix-forward — underline travels the wire as `++text++` (Lexical
  // TextFormatTransformer with tag `++`). We convert it to `<u>text</u>` in
  // a pre-process pass below so combined formats like `++***foo***++`
  // arrive as `<u><em><strong>foo</strong></em></u>` after react-markdown
  // parses the inner emphasis. rehype-raw is what makes the inline HTML
  // pass through unmolested.
  u({ children }) {
    return <u className="md-underline">{children}</u>;
  }
};

/**
 * Pre-process the markdown content to swap `++text++` for `<u>text</u>`.
 *
 * Why a string pre-process instead of a remark plugin: `++` is not a
 * CommonMark delimiter, so by the time the AST exists, the `++` markers
 * are already plain text nodes that may surround sibling element nodes
 * (e.g. `++***foo***++` ⇒ ["++", emphasis>strong>"foo", "++"]). Doing the
 * swap at the source level reduces that to plain HTML which react-markdown
 * + rehype-raw can render correctly with whatever inner markdown sits
 * between the tags. Lazy match (`.+?`) so adjacent ranges in the same
 * paragraph don't collapse into one giant underline.
 *
 * Skip code blocks and inline code — markdown allows `++text++` literals
 * inside backticks. We protect those by splitting on triple-backtick code
 * fences first, then by inline `` `…` `` segments, swapping only outside
 * the protected segments.
 */
function preprocessUnderline(source) {
  if (typeof source !== 'string' || source.indexOf('++') === -1) return source;
  // Split into alternating non-code / code segments. Triple-backtick fences
  // and single-backtick inline spans are preserved verbatim.
  const segments = source.split(/(```[\s\S]*?```|`[^`\n]+?`)/g);
  return segments
    .map((seg) => {
      if (seg.startsWith('`')) return seg;
      return seg.replace(/\+\+(.+?)\+\+/g, '<u>$1</u>');
    })
    .join('');
}

// KAP-303 — react-markdown's default `urlTransform` filters non-http(s) URL
// schemes for safety, which strips the `user:` scheme out of the ADR-0044
// mention sentinel `[Display Name](user:uuid)`. The renderer below then
// sees `href=""` and can't detect the mention. We whitelist `user:` (and
// the reserved `channel:` form) so the chip detection works; everything
// else still passes through the default sanitization for safety.
function urlTransform(url) {
  if (typeof url === 'string' && (url.startsWith('user:') || url.startsWith('channel:'))) {
    return url;
  }
  // Default behavior (mirrors react-markdown's defaultUrlTransform).
  if (typeof url !== 'string') return '';
  if (/^(?:https?|ircs?|mailto|xmpp):/i.test(url)) return url;
  if (/^[^:]*$/.test(url) || /^[^:]*\//.test(url) || url.startsWith('#') || url.startsWith('/')) return url;
  return '';
}

function MarkdownRenderer({ content }) {
  if (!content) return null;

  const transformed = preprocessUnderline(content);

  return (
    <div className="md-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={markdownComponents}
        urlTransform={urlTransform}
      >
        {transformed}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownRenderer;
